/**
 * Utility functions for processing citation tags in markdown content.
 * Supports both knowledge base citations (asset_*) and web search citations (web_toolexecution_*).
 */

/**
 * Parsed web citation containing toolId and index
 */
export interface ParsedWebCitation {
  toolId: string; // e.g., "web_toolexecution_abc123"
  index: number; // e.g., 22
  raw: string; // original citation string
}

/**
 * Validates if a string is a valid asset ID format
 *
 * @param str - The string to validate
 * @returns True if the string is a valid asset ID format
 */
export const isValidAssetId = (str: string): boolean => {
  return typeof str === "string" && str.trim().startsWith("asset_");
};

/**
 * Validates if a string is a valid web citation format (e.g., "web_toolexecution_abc123_22")
 *
 * @param str - The string to validate
 * @returns True if the string is a valid web citation format
 */
export const isValidWebCitation = (str: string): boolean => {
  if (typeof str !== "string") return false;
  const trimmed = str.trim();
  // Format: web_toolexecution_<toolId>_<index>
  return /^web_toolexecution_[a-zA-Z0-9]+_\d+$/.test(trimmed);
};

/**
 * Gets the type of citation ("asset" or "web")
 *
 * @param citation - The citation string
 * @returns The citation type, or null if not valid
 */
export const getCitationType = (citation: string): "asset" | "web" | null => {
  if (isValidAssetId(citation)) return "asset";
  if (isValidWebCitation(citation)) return "web";
  return null;
};

/**
 * Parses a web citation to extract toolId and index
 *
 * @param citation - The web citation string (e.g., "web_toolexecution_abc123_22")
 * @returns Parsed citation object, or null if not a valid web citation
 */
export const parseWebCitation = (citation: string): ParsedWebCitation | null => {
  if (!isValidWebCitation(citation)) return null;

  // Match format: web_toolexecution_<toolId>_<index>
  const match = citation.match(/^web_toolexecution_([a-zA-Z0-9]+)_(\d+)$/);
  if (!match) return null;

  const toolId = `web_toolexecution_${match[1]}`;
  const index = Number.parseInt(match[2], 10);

  return {
    toolId,
    index,
    raw: citation,
  };
};

/**
 * Extracts the index number from a web citation (e.g., "web_toolexecution_abc123_22" returns 22)
 *
 * @param citation - The web citation string
 * @returns The index number, or null if not a valid web citation
 * @deprecated Use parseWebCitation instead for full parsing
 */
export const extractWebCitationIndex = (citation: string): number | null => {
  const parsed = parseWebCitation(citation);
  return parsed?.index ?? null;
};
